import { useEffect, useMemo, useState } from "react";
import "../../styles/gifting/gf-base.css";
import "../../styles/gifting/gf-pipeline.css";
import "../../styles/gifting/gf-tabs.css";
import "../../styles/gifting/gf-panel-v2.css";
import type { GfAssetFilter, GfNav, GfNavState, GfTab, GfTabProps, GfView } from "../../types/gifting";
import { GfErrorBanner, GfSkeleton } from "./ui/GfUi";
import { GiftingHeader, type GfMoreKind } from "./GiftingHeader";
import { HelpModal } from "./modals/HelpModal";
import { LinksModal } from "./modals/LinksModal";
import { TemplatesModal } from "./modals/TemplatesModal";
import { GF_TABS } from "./gfUtil";
import { isActionKey } from "../../lib/giftingRules";
import { useGiftingData } from "./useGiftingData";
import { useGfToast } from "./useGfToast";
import { DashboardTab } from "./tabs/DashboardTab";
import { PipelineTab } from "./tabs/PipelineTab";
import { CreatorsTab } from "./tabs/CreatorsTab";
import { ShipTab } from "./tabs/ShipTab";
import { RatesTab } from "./tabs/RatesTab";
import { AssetsTab } from "./tabs/AssetsTab";
import { SalesTab } from "./tabs/SalesTab";
import { SettleTab } from "./tabs/SettleTab";
import { EntryPanel } from "./panels/EntryPanel";
import { CreatorPanel } from "./panels/CreatorPanel";

// ---- URL 状態（plan §4-3）。mock=1 など他のクエリは保持する ---------------------------------

const ENTRY_PANEL_TABS: readonly GfTab[] = ["deals", "ship", "dash"];
const CREATOR_PANEL_TABS: readonly GfTab[] = ["creators", "settle", "rates"];
const VIEWS: readonly GfView[] = ["kanban", "list", "archive"];
const ASSET_FILTERS: readonly GfAssetFilter[] = ["all", "granted", "requested", "expiring"];

function readUrlState(): GfNavState {
  const p = new URLSearchParams(window.location.search);
  const tab = p.get("gf") as GfTab | null;
  const view = p.get("gfView") as GfView | null;
  const asset = p.get("gfAsset") as GfAssetFilter | null;
  const act = p.get("gfAct");
  return {
    tab: tab && GF_TABS.some((t) => t.key === tab) ? tab : "dash",
    // v2: 既定は一覧（カンバンは補助）
    view: view && VIEWS.includes(view) ? view : "list",
    campaign: p.get("gfCampaign") || "all",
    sel: p.get("gfSel") || null,
    creator: p.get("gfCreator") || null,
    rateCampaign: p.get("gfRate") || null,
    asset: asset && ASSET_FILTERS.includes(asset) ? asset : "all",
    act: isActionKey(act) ? act : null,
    compose: null,
  };
}

function writeUrlState(s: GfNavState): void {
  const p = new URLSearchParams(window.location.search);
  p.set("gf", s.tab);
  const setOrDelete = (key: string, value: string | null, dflt: string | null) => {
    if (value && value !== dflt) p.set(key, value);
    else p.delete(key);
  };
  setOrDelete("gfView", s.view, "list");
  setOrDelete("gfCampaign", s.campaign, "all");
  setOrDelete("gfSel", s.sel, null);
  setOrDelete("gfCreator", s.creator, null);
  setOrDelete("gfRate", s.rateCampaign, null);
  setOrDelete("gfAsset", s.asset, "all");
  setOrDelete("gfAct", s.act, null);
  window.history.replaceState(null, "", `${window.location.pathname}?${p.toString()}`);
}

export function GiftingPage() {
  const [state, setState] = useState<GfNavState>(() => readUrlState());
  useEffect(() => writeUrlState(state), [state]);

  const { board, data, nowIso, month, refresh, retry, refreshError } = useGiftingData();
  const { toast, toastNode } = useGfToast();
  const [more, setMore] = useState<GfMoreKind | null>(null);
  const closeMore = () => setMore(null);

  const nav = useMemo<GfNav>(
    () => ({
      state,
      set: (patch) => setState((s) => ({ ...s, ...patch })),
      openEntry: (dealId, tab = "deals") => setState((s) => ({ ...s, tab, sel: dealId, creator: null })),
      openCreator: (influencerId) =>
        setState((s) => ({
          ...s,
          creator: influencerId,
          sel: null,
          tab: CREATOR_PANEL_TABS.includes(s.tab) ? s.tab : "creators",
        })),
      closePanel: () => setState((s) => ({ ...s, sel: null, creator: null, compose: null })),
    }),
    [state]
  );

  const tabProps: GfTabProps | null = data ? { data, nowIso, month, nav, refresh, toast } : null;

  const renderTab = (p: GfTabProps) => {
    switch (state.tab) {
      case "dash":
        return <DashboardTab {...p} />;
      case "deals":
        return <PipelineTab {...p} />;
      case "creators":
        return <CreatorsTab {...p} />;
      case "ship":
        return <ShipTab {...p} />;
      case "rates":
        return <RatesTab {...p} />;
      case "assets":
        return <AssetsTab {...p} />;
      case "sales":
        return <SalesTab {...p} />;
      case "settle":
        return <SettleTab {...p} />;
    }
  };

  return (
    <div className="gf-root">
      <GiftingHeader data={data} nav={nav} onMore={setMore} />
      <div className="gf-body">
        {board.status === "loading" && (
          <div className="gf-loading" aria-busy="true">
            <GfSkeleton height={32} width={220} />
            <GfSkeleton height={44} />
            <GfSkeleton height={260} />
          </div>
        )}
        {board.status === "error" && (
          <GfErrorBanner message={`ギフティングのデータを読み込めませんでした。${board.message}`} onRetry={retry} />
        )}
        {board.status === "ok" && refreshError && (
          <GfErrorBanner
            message={`最新の状態を取得できませんでした（表示は前回の内容です）。${refreshError}`}
            onRetry={() => void refresh()}
          />
        )}
        {tabProps && renderTab(tabProps)}
      </div>
      {tabProps && state.sel && ENTRY_PANEL_TABS.includes(state.tab) && (
        <EntryPanel key={state.sel} {...tabProps} dealId={state.sel} />
      )}
      {tabProps && state.creator && CREATOR_PANEL_TABS.includes(state.tab) && (
        <CreatorPanel key={state.creator} {...tabProps} creatorId={state.creator} />
      )}
      <TemplatesModal open={more === "templates"} onClose={closeMore} toast={toast} />
      {data && <LinksModal open={more === "links"} onClose={closeMore} data={data} nav={nav} toast={toast} />}
      <HelpModal open={more === "help"} onClose={closeMore} />
      {toastNode}
    </div>
  );
}
